import { useMemo, useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ClubLogosDialog } from "@/components/ClubLogosDialog";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SIMPLE_DEFAULT, simpleOf } from "@/components/UscLayout";
import { parseSchedule, sortPoster, carryStyles, splitAcademySeniors, keepManualRows } from "@/lib/posterTemplate";
import { toast } from "sonner";
import { ClipboardPaste, Sparkles, Loader2, Trophy, AlignLeft, AlignCenter, AlignRight, Bold, Italic, CaseUpper, Plus, Trash2, Images, ImagePlus } from "lucide-react";
import { api } from "@/lib/api";
import { parseFfa, fillScores } from "@/lib/ffaImport";
import { FfaHelp } from "@/components/FfaHelp";
import { dupIds, mergeParsed } from "@/lib/mergeImport";
import { SelectionPanel, resetAllPositions, hasMoves } from "@/components/SelectionPanel";
import { ImportPreview, withoutExcluded } from "@/components/ImportPreview";
import { upsertSplit } from "@/lib/splitPosters";
import { Checkbox } from "@/components/ui/checkbox";
import { useClubLogos, findLogo, loadClubLogos } from "@/lib/clubLogos";

const IMPORT_MODES = [["ffa", "Site FFA (favori)"], ["image", "Lire une image"], ["ai", "Copier-coller + IA"], ["text", "Texte simple"]];
const swapSides = (m) => { const [a, ...b] = m.split(" - "); return b.length ? `${b.join(" - ")} - ${a}` : m; };
const mapPreview = (p, fn) => ({ ...p, top: { ...p.top, rows: p.top.rows.map(fn) }, cols: p.cols.map((c) => ({ ...c, rows: c.rows.map(fn) })), footer: p.footer.map(fn) });
const FONTS = [["Bebas Neue", "Bebas Neue"], ["Oswald", "Oswald"], ["Anton", "Anton"], ["Teko", "Teko"], ["Russo One", "Russo One"], ["Barlow Condensed", "Barlow Condensed"]];

const EXAMPLE = `VENDREDI 09/10
U13 C.OL. TRIVIÈRES - USC 18H00
SAMEDI 10/10
U6 F.C. VACRESSE - USC F.G.
U7 USC - F.C. AEDEC HYON 11H30
DIMANCHE 11/10
U12A R.SP.B. FRAMERIES - USC 11H15
P4 R.C.S. AULNOIS. - USC 15H
D3 USC - R. ARQ.NAMUR 15H`;

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const withIds = (rows = []) => rows.map((r) => ({ score: "", ...r, id: uid() }));

const fromAi = (res) => ({
  top: { header: res.top?.header || "", rows: withIds(res.top?.rows) },
  cols: (res.columns || []).map((c) => ({ id: uid(), header: c.header || "", rows: withIds(c.rows) })),
  footer: withIds(res.footer),
});

const countOf = ({ top, cols, footer }) => top.rows.length + cols.reduce((n, c) => n + c.rows.length, 0) + footer.length;

const scoreRows = (rows) => rows.map((r) => ({ ...r, time: r.score || "pas de score" }));
const asScorePreview = (p) => ({
  ...p,
  top: { ...p.top, rows: scoreRows(p.top.rows) },
  cols: p.cols.map((c) => ({ ...c, rows: scoreRows(c.rows) })),
  footer: p.footer.map((r) => ({ ...r, day: "", time: r.score || "pas de score" })),
});

const ImportDialog = ({ open, onOpenChange, data, setData }) => {
  const [mode, setMode] = useState("ffa");
  const logos = useClubLogos();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(() => new Set());
  const [alsoSplit, setAlsoSplit] = useState(true);
  const [image, setImage] = useState(null);
  const imgRef = useRef(null);
  const onSwap = (id) => setPreview((p) => mapPreview(p, (r) => (r.id === id ? { ...r, match: swapSides(r.match), home_guess: false } : r)));
  const live = useMemo(() => (mode === "ffa" ? parseFfa(text, data.team_name || "USC") : mode === "text" && text.trim() ? parseSchedule(text) : null),
    [mode, text, data.team_name]);
  const scoreMode = false;
  const shown = mode === "ai" || mode === "image" ? preview : live;
  const chosen = () => withoutExcluded(shown, excluded);
  const shownScores = useMemo(() => (scoreMode && live ? asScorePreview(live) : null), [scoreMode, live]);

  const applyScores = () => {
    const parsed = chosen();
    if (!parsed) return toast.error("Texte non reconnu : utilisez le favori « Envoyer à l'affiche » sur ffa.be puis collez ici");
    if (!parsed.scoresAvailable) return toast.error("Le favori n'a pas pu récupérer les scores sur ffa.be — réinstallez le favori ou tapez les scores à la main");
    const { next, filled, missing } = fillScores(data, parsed);
    if (!filled) return toast.error("Aucun score trouvé pour les matchs de cette affiche");
    setData(next);
    toast.success(`${filled} score(s) rempli(s)${missing ? `, ${missing} match(s) sans score` : ""} — pensez à enregistrer`);
    setText("");
    setExcluded(new Set());
    onOpenChange(false);
  };

  const apply = (parsed) => {
    const { top, cols, footer } = parsed;
    const count = countOf(parsed);
    if (!count) return toast.error("Aucun match reconnu — vérifiez le texte");
    const [c1, ...rest] = cols;
    const c2 = rest.length ? { ...rest[0], rows: rest.flatMap((c) => c.rows) } : null;
    const empty = (i) => ({ id: data.columns?.[i]?.id || uid(), header: "", rows: [] });
    const day = (c) => (c?.header || "").trim().split(/\s+/)[0].toUpperCase();
    const keep = (nc, i) => {
      const old = (data.columns || []).find((o) => day(o) && day(o) === day(nc)) || data.columns?.[i] || {};
      const { dx, dy, hs, split } = old;
      return { ...nc, ...(dx !== undefined && { dx }), ...(dy !== undefined && { dy }), ...(hs && { hs }), ...(split !== undefined && { split }) };
    };
    const full0 = carryStyles(data, sortPoster({
      ...data,
      top: { ...(data.top || {}), header: top.header || data.top?.header || "MATCHS EN SEMAINE", rows: top.rows, show: top.rows.length > 0 },
      columns: [keep(c1 || empty(0), 0), keep(c2 || empty(1), 1)],
      show_columns: cols.some((c) => c.rows.length) || data.show_columns !== false,
      show_footer: footer.length > 0 || data.show_footer !== false,
      show_title: true,
      footer,
      overlays: [],
    }));
    const { poster: full, kept } = keepManualRows(data, full0);
    if (kept) toast.info(`${kept} match(s) ajouté(s) à la main conservé(s) (même semaine)`);
    const split = alsoSplit && data.id && !data.split_of;
    const next = split ? { ...splitAcademySeniors(full).academy, name: data.name, title: data.title } : full;
    setData(next);
    toast.success(`${count} match(s) importé(s) — pensez à enregistrer`);
    if (split) upsertSplit(full, ["senior"])
      .then(() => toast.success("Matchs seniors envoyés sur l'affiche « Seniors » (même style) — enregistrez aussi cette affiche", { duration: 8000 }))
      .catch(() => toast.error("Échec de la mise à jour de l'affiche Seniors"));
    setText("");
    setPreview(null);
    setExcluded(new Set());
    onOpenChange(false);
  };

  const analyze = async () => {
    if (mode === "image" ? !image : !text.trim()) return toast.error(mode === "image" ? "Choisissez d'abord une image" : "Collez d'abord le texte de la page");
    setLoading(true);
    try {
      const form = new FormData();
      if (mode === "image") { form.append("file", image); form.append("team_name", data.team_name || "USC"); }
      const { data: res } = mode === "image"
        ? await api.post("/schedule/parse-image", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 })
        : await api.post("/schedule/parse", { text, team_name: data.team_name || "USC" });
      const parsed = fromAi(res);
      if (!countOf(parsed)) toast.error(mode === "image" ? "Aucun match trouvé sur cette image" : "Aucun match du club trouvé dans ce texte");
      setPreview(parsed);
      const dups = mode === "image" ? dupIds(data, parsed) : [];
      setExcluded(new Set(dups));
      if (dups.length) toast.info(`${dups.length} match(s) déjà sur l'affiche : décoché(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'analyse IA");
    } finally {
      setLoading(false);
    }
  };

  const addFromImage = () => {
    const parsed = chosen();
    const n = countOf(parsed);
    if (!n) return toast.error("Aucun nouveau match à ajouter");
    setData(mergeParsed(data, parsed, uid));
    toast.success(`${n} match(s) ajouté(s)${excluded.size ? `, ${excluded.size} ignoré(s)` : ""} — pensez à enregistrer`);
    setImage(null); setPreview(null); setExcluded(new Set()); onOpenChange(false);
  };

  const switchMode = (m) => { setMode(m); setPreview(null); setExcluded(new Set()); setImage(null); };
  const pickImage = (f) => { if (!f) return; setImage(f); setPreview(null); setExcluded(new Set()); if (imgRef.current) imgRef.current.value = ""; };

  const applyFfa = async () => {
    const parsed = chosen();
    if (!parsed) return toast.error("Texte non reconnu : utilisez le favori « Envoyer à l'affiche » sur ffa.be puis collez ici");
    const missing = parsed.logos.filter((l) => !findLogo(logos, l.name));
    apply(parsed);
    if (!missing.length) return;
    setLoading(true);
    const added = await Promise.all(missing.map((l) =>
      api.post("/club-logos", { name: l.name, source_url: l.url, allow_external: true }).then((r) => r.data).catch(() => null)));
    setLoading(false);
    await loadClubLogos(true);
    toast.success(`${added.filter(Boolean).length} logo(s) de club ajouté(s)`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="import-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">Importer le calendrier</DialogTitle></DialogHeader>
        <div className="flex gap-1 rounded-lg bg-secondary p-1">
          {IMPORT_MODES.map(([k, l]) => (
            <button key={k} onClick={() => switchMode(k)} data-testid={`import-mode-${k}`}
              className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${mode === k ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>
              {k === "ai" && <Sparkles className="mr-1 inline h-3 w-3" />}{l}
            </button>
          ))}
        </div>
        {mode === "ffa" && <FfaHelp />}
        <p className="text-xs text-muted-foreground" hidden={mode === "ffa" || mode === "image"}>
          {mode === "text"
            ? "Une ligne par jour (VENDREDI, SAMEDI, DIMANCHE…) puis une ligne par match : catégorie, match, heure (ou F.G.). Les lignes P4/D3… vont automatiquement en bas."
            : "Sélectionnez tout le calendrier sur le site de la fédération (Ctrl+A, Ctrl+C) et collez-le ici, même désordonné. L'IA garde uniquement les matchs du club et remplace son nom par votre équipe."}
          {" "}L'import remplace les matchs actuels.
        </p>
        {mode === "image" && (
          <div className="space-y-2" data-testid="image-read-panel">
            <p className="text-xs text-muted-foreground">Envoyez une photo ou une capture d'affiche : l'IA lit les matchs (catégorie, heure, adversaire, jour). Vérifiez la liste, utilisez ⇄ pour inverser domicile / extérieur, décochez les erreurs. Les matchs sont <b>ajoutés</b> à ceux de l'affiche (ex. après l'import FFA) ; ceux déjà présents sont décochés automatiquement.</p>
            <input ref={imgRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => pickImage(e.target.files?.[0])} data-testid="image-read-input" />
            <div className="flex items-center gap-3">
              <Button variant="secondary" size="sm" onClick={() => imgRef.current?.click()} data-testid="image-read-pick-button">
                <ImagePlus className="mr-1 h-4 w-4" /> {image ? "Changer d'image" : "Choisir une image"}
              </Button>
              {image && <img src={URL.createObjectURL(image)} alt="" className="h-16 w-16 rounded border border-border object-cover" data-testid="image-read-thumb" />}
              {image && <span className="truncate text-xs text-muted-foreground">{image.name}</span>}
            </div>
          </div>
        )}
        {mode !== "image" && <Textarea value={text} onChange={(e) => { setText(e.target.value); setPreview(null); setExcluded(new Set()); }} rows={shown ? 4 : 12}
          placeholder={mode === "text" ? EXAMPLE : mode === "ffa" ? "Collez ici (Ctrl+V) le résultat copié par le favori…" : "Collez ici le contenu de la page du site…"}
          className="font-mono text-xs" data-testid="import-textarea" />}
        {shown && <ImportPreview parsed={shownScores || shown} excluded={excluded} setExcluded={setExcluded} onSwap={mode === "image" || mode === "ai" ? onSwap : undefined} />}
        {!data.split_of && !scoreMode && mode !== "image" && (
          <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="import-also-split-label">
            <Checkbox checked={alsoSplit} onCheckedChange={(v) => setAlsoSplit(!!v)} data-testid="import-also-split-checkbox" />
            Matchs seniors sur l'affiche « Seniors » séparée (même style)
          </label>
        )}
        <div className="flex justify-end gap-2">
          {mode === "ffa" ? (
            <>
              <Button variant={scoreMode ? "secondary" : "default"} onClick={applyFfa} disabled={loading} data-testid="import-ffa-apply-button">
                {loading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} {scoreMode ? "Remplacer les matchs" : "Importer les matchs et logos"}
              </Button>
              {scoreMode && (
                <Button onClick={applyScores} data-testid="import-fill-scores-button">
                  <Trophy className="mr-1 h-4 w-4" /> Remplir les scores
                </Button>
              )}
            </>
          ) : mode === "text" ? (
            <>
              <Button variant="ghost" onClick={() => setText(EXAMPLE)} data-testid="import-example-button">Exemple</Button>
              <Button onClick={() => apply(chosen() || parseSchedule(text))} data-testid="import-apply-button">Importer</Button>
            </>
          ) : (
            <>
              <Button variant={preview ? "ghost" : "default"} onClick={analyze} disabled={loading} data-testid="import-analyze-button">
                {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
                {loading ? (mode === "image" ? "Lecture de l'image… (jusqu'à 1 min)" : "Analyse en cours…") : preview ? "Relancer l'analyse" : mode === "image" ? "Lire l'image" : "Analyser"}
              </Button>
              {preview && countOf(preview) > 0 && (
                <Button onClick={() => (mode === "image" ? addFromImage() : apply(chosen()))} data-testid="import-ai-apply-button">{mode === "image" ? "Ajouter à l'affiche" : "Valider et remplir l'affiche"}</Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const COLORS = [["title_color", "Titre"], ["head_color", "Jours"], ["cat_color", "Catégories"], ["time_color", "Heures"], ["text_color", "Matchs"], ["club_color", "Mon club"]];
const ALIGNS = [["left", AlignLeft, "Gauche (colonnes)"], ["center", AlignCenter, "Centre"], ["right", AlignRight, "Droite"]];
const FLAGS = [["bold", Bold, "Gras"], ["italic", Italic, "Italique"], ["upper", CaseUpper, "Majuscules"]];

const Row = ({ label, children }) => (
  <div className="flex items-center gap-2"><Label className="w-20 shrink-0 text-xs">{label}</Label>{children}</div>
);
const Range = ({ value, min, max, step = 1, unit, onChange, testid }) => (
  <>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="flex-1" data-testid={testid} />
    <span className="w-10 font-mono text-[10px] text-muted-foreground">{value}{unit}</span>
  </>
);

let teamTimer;
const saveTeams = (teams) => {
  clearTimeout(teamTimer);
  teamTimer = setTimeout(() => api.put("/team-colors", { teams }).catch(() => toast.error("Couleurs des équipes non mémorisées")), 600);
};

const TeamColors = ({ list, onChange }) => {
  const set = (i, patch) => onChange(list.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  return (
    <div className="space-y-2 border-t border-border pt-3" data-testid="team-colors">
      <p className="text-xs font-semibold">Couleur par équipe</p>
      {list.map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={t.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="ex. FC GHLIN" className="h-8 flex-1 text-xs" data-testid={`team-color-name-${i}`} />
          <input type="color" value={t.color} onChange={(e) => set(i, { color: e.target.value })} data-testid={`team-color-value-${i}`}
            className="h-8 w-10 cursor-pointer rounded border border-border bg-transparent" />
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`team-color-delete-${i}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button size="sm" variant="secondary" onClick={() => onChange([...list, { name: "", color: "#ff5a5a" }])} data-testid="add-team-color-button">
        <Plus className="mr-1 h-4 w-4" /> Ajouter une équipe
      </Button>
    </div>
  );
};

export const UscControls = ({ data, setData }) => {
  const [open, setOpen] = useState(false);
  const [logosOpen, setLogosOpen] = useState(false);
  const s = simpleOf(data);
  const setS = (patch) => setData({ ...data, band: { ...(data.band || {}), ...patch } });
  return (
    <div className="w-full space-y-3 border-t border-border pt-3" data-testid="usc-controls">
      <Button size="sm" className="w-full" onClick={() => setOpen(true)} data-testid="import-schedule-button">
        <ClipboardPaste className="mr-1 h-4 w-4" /> Importer le calendrier
      </Button>
      <SelectionPanel data={data} setData={setData} fonts={FONTS} />
      <div className="flex items-center gap-2" data-testid="sort-controls">
        <Label className="w-20 shrink-0 text-xs">Trier les lignes</Label>
        {[["cat", "Par catégorie"], ["time", "Par heure"]].map(([by, l]) => (
          <Button key={by} size="sm" variant={(data.band?.sort_by || "cat") === by ? "default" : "secondary"} className="h-8 flex-1 text-xs"
            onClick={() => { setData(sortPoster({ ...data, band: { ...(data.band || {}), sort_by: by } })); toast.success(`Lignes triées ${l.toLowerCase()}`); }}
            data-testid={`sort-by-${by}`}>{l}</Button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={s.show_logos} onCheckedChange={(show_logos) => setS({ show_logos })} data-testid="show-logos-toggle" />
        <Label className="flex-1 text-xs">Afficher les logos</Label>
        <Button size="sm" variant="secondary" onClick={() => setLogosOpen(true)} data-testid="club-logos-button">
          <Images className="mr-1 h-4 w-4" /> Logos des clubs
        </Button>
      </div>
      <ClubLogosDialog open={logosOpen} onOpenChange={setLogosOpen} data={data} />
      <Row label="Fond visible"><Range value={s.bg_opacity} min={0} max={100} unit="%" onChange={(bg_opacity) => setS({ bg_opacity })} testid="bg-opacity-slider" /></Row>
      <div className="flex items-center justify-between border-t border-border pt-3">
        <p className="text-xs font-semibold">Disposition des blocs</p>
        {hasMoves(data) && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setData(resetAllPositions(data))} data-testid="reset-moves-button">Remettre les positions</Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">Cliquez une ligne pour la sélectionner (Maj/Ctrl + clic pour en ajouter), puis glissez-la : toutes les lignes sélectionnées bougent ensemble. Double-clic pour modifier le texte. Les titres se déplacent avec leur poignée orange ; « 2 colonnes » apparaît en survolant le titre d'un jour. Échap désélectionne.</p>
      <Row label="Semaine">
        <select value={s.week_pos} onChange={(e) => setS({ week_pos: e.target.value })} data-testid="week-pos-select"
          className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-xs">
          <option value="top">En haut</option><option value="bottom">En bas (sous le week-end)</option>
        </select>
      </Row>
      <Row label="Week-end">
        <select value={s.days_layout} onChange={(e) => setS({ days_layout: e.target.value })} data-testid="days-layout-select"
          className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-xs">
          <option value="row">Samedi à gauche, dimanche à droite</option><option value="column">Samedi au-dessus du dimanche</option>
        </select>
      </Row>
      <p className="border-t border-border pt-3 text-xs font-semibold">Texte (tous les textes)</p>
      <Row label="Police">
        <select value={s.font} onChange={(e) => setS({ font: e.target.value })} data-testid="font-select"
          className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-xs">
          {FONTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Row>
      <Row label="Taille"><Range value={s.size} min={16} max={60} unit="px" onChange={(size) => setS({ size })} testid="text-size-slider" /></Row>
      <Row label="Entre matchs"><Range value={s.line_gap} min={0} max={1} step={0.05} unit="em" onChange={(line_gap) => setS({ line_gap })} testid="line-gap-slider" /></Row>
      <Row label="Lettres"><Range value={s.ls} min={-2} max={10} step={0.5} unit="px" onChange={(ls) => setS({ ls })} testid="letter-spacing-slider" /></Row>
      <div className="flex flex-wrap gap-1">
        {ALIGNS.map(([a, Icon, l]) => (
          <Button key={a} size="sm" variant={s.align === a ? "default" : "secondary"} className="h-8 w-9 p-0" title={l}
            onClick={() => setS({ align: a })} data-testid={`text-align-${a}`}><Icon className="h-4 w-4" /></Button>
        ))}
        <span className="mx-1 w-px bg-border" />
        {FLAGS.map(([k, Icon, l]) => (
          <Button key={k} size="sm" variant={s[k] ? "default" : "secondary"} className="h-8 w-9 p-0" title={l}
            onClick={() => setS({ [k]: !s[k] })} data-testid={`text-${k}`}><Icon className="h-4 w-4" /></Button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-y-2">
        {COLORS.map(([k, l]) => (
          <label key={k} className="flex flex-col items-center gap-1 text-[11px]">
            <input type="color" value={s[k]} onChange={(e) => setS({ [k]: e.target.value })} data-testid={`color-${k}`}
              className="h-8 w-12 cursor-pointer rounded border border-border bg-transparent" />{l}
          </label>
        ))}
      </div>
      <TeamColors list={(data.band || {}).team_colors || []} onChange={(team_colors) => { setS({ team_colors }); saveTeams(team_colors); }} />
      <p className="text-[11px] text-muted-foreground">« Mon club » colore automatiquement le nom de votre équipe ({data.team_name || "USC"}) dans les matchs. Survolez une ligne pour l'aligner ou la supprimer.</p>
      <button onClick={() => setS({ ...SIMPLE_DEFAULT, bg_opacity: s.bg_opacity, week_pos: s.week_pos, days_layout: s.days_layout, line_gap: s.line_gap })} className="text-xs text-muted-foreground underline-offset-2 hover:underline" data-testid="reset-text-button">
        Réglages du texte d'origine
      </button>
      <ImportDialog open={open} onOpenChange={setOpen} data={data} setData={setData} />
    </div>
  );
};
